import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
const items = [{
  title: 'Emotion Wheel',
  description: 'A colorful spinning wheel that helps kids explore, identify, and talk about different emotions through playful interaction.',
  image: 'https://cdn.magicpatterns.com/uploads/kJ1SnbBbRRkdLNbdgzxupL/image.png',
  color: 'text-primary'
}, {
  title: 'Emotion Traffic Light',
  description: 'A visual stop-think-respond tool that teaches children to pause and manage their reactions, just like a traffic light.',
  image: 'https://cdn.magicpatterns.com/uploads/pBMyoPtwFvcULHvdnGmWjd/image.png',
  color: 'text-success'
}, {
  title: 'Faces Game',
  description: 'A fun matching game where kids connect facial expressions to emotions, building their ability to read and understand feelings.',
  image: 'https://cdn.magicpatterns.com/uploads/5DfmouFu9BS7wGgnLpZyJa/image.png',
  color: 'text-accent'
}, {
  title: "Card Game 'Inside Out'",
  description: 'Inspired by the world of emotions, this card game helps children explore their inner feelings through creative play and storytelling.',
  image: 'https://cdn.magicpatterns.com/uploads/6bAoTpqPE4pPHMWc7zEqtm/image.png',
  color: 'text-secondary'
}, {
  title: 'Sensations Game',
  description: 'Helps kids connect physical body sensations to their emotions, building powerful mind-body awareness and self-regulation skills.',
  image: 'https://cdn.magicpatterns.com/uploads/j7abfZicdsoYLiL8fB9K4t/image.png',
  color: 'text-primary'
}, {
  title: 'Expression Theatre',
  description: 'A creative role-play activity where children act out different emotions, building empathy, confidence, and emotional vocabulary.',
  image: 'https://cdn.magicpatterns.com/uploads/agBoRAqWKwc9hg3AJNjCDN/image.png',
  color: 'text-accent'
}, {
  title: 'Emotions Path Board Game',
  description: 'An exciting board game adventure where every step brings a new emotional scenario to discuss, solve, and learn from.',
  image: 'https://cdn.magicpatterns.com/uploads/p3JEFU7yUoNXru2z1TAxyr/image.png',
  color: 'text-secondary'
}, {
  title: 'Guess the Emotion Game',
  description: 'A lively guessing game where players describe or act out emotions while others try to identify them — perfect for family fun.',
  image: 'https://cdn.magicpatterns.com/uploads/nRd59AY8btxk8qMymc26Lf/image.png',
  color: 'text-success'
}, {
  title: 'Calm Box',
  description: 'A curated collection of calming strategies, breathing exercises, and sensory tools kids can use when emotions feel overwhelming.',
  image: 'https://cdn.magicpatterns.com/uploads/dDgcZsbbA5Bjj6hJEKdbwy/image.png',
  color: 'text-secondary'
}, {
  title: 'Exclusive Emotions UNO',
  description: 'Our signature emotions-themed UNO card game that makes learning about feelings exciting, competitive, and unforgettable.',
  image: 'https://cdn.magicpatterns.com/uploads/cwaY8JUMxdma6SLmCpsH2b/image.png',
  isExclusive: true,
  color: 'text-primary'
}];
export function WhatIsIncluded() {
  return <section id="included" className="py-24 bg-peach relative" data-id="element-282">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-283">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-284">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-dark mb-6" data-id="element-285">
            What Does Your Kit Include?
          </h2>
          <p className="text-xl text-muted" data-id="element-286">
            Everything you need to start building your child's emotional
            intelligence — all in one printable kit.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16" data-id="element-287">
          {items.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.06
        }} className="group bg-white rounded-3xl p-4 shadow-sm hover:shadow-warm transition-all duration-300 flex flex-col h-full border border-black/5" data-id="element-288">
              <div className="relative w-full aspect-[4/3] mb-5 overflow-hidden rounded-2xl bg-gray-100" data-id="element-289">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-290" />

                {/* Subtle overlay for better contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-291"></div>

                {/* Item Number Badge */}
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-lg text-xs font-bold text-dark shadow-sm" data-id="element-292">
                  Item {index + 1}
                </div>

                {/* Exclusive Badge */}
                {item.isExclusive && <div className="absolute bottom-3 right-3 bg-accent text-dark text-xs font-extrabold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300" data-id="element-293">
                    <Star className="w-3.5 h-3.5 fill-dark" data-id="element-294" /> EXCLUSIVE
                  </div>}
              </div>

              <div className="flex-grow flex flex-col px-2 pb-2" data-id="element-295">
                <h3 className={`text-lg font-heading font-bold mb-2 ${item.color || 'text-dark'}`} data-id="element-296">
                  {item.title}
                </h3>
                <p className="text-muted text-sm leading-relaxed flex-grow" data-id="element-297">
                  {item.description}
                </p>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center" data-id="element-298">
          <a href="#cta" className="inline-flex justify-center items-center bg-primary hover:bg-primary/90 text-white px-10 py-5 rounded-full font-bold text-xl transition-all hover:shadow-warm hover:-translate-y-1" data-id="element-299">
            Get Everything for Just $14.99
          </a>
        </motion.div>
      </div>
    </section>;
}